import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { PhX } from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';

// ---------------------------------------------------------------------------
// Sub-components: Header, Body, Footer
// ---------------------------------------------------------------------------

interface DrawerHeaderSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const DrawerHeader: TOC<DrawerHeaderSignature> = <template>
  <div
    data-test-drawer-header
    class="flex-none pl-6 pr-12 py-4 border-b tio-border-default dark:border-gray-700"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

interface DrawerBodySignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const DrawerBody: TOC<DrawerBodySignature> = <template>
  <div data-test-drawer-body class="flex-1 overflow-y-auto px-6 py-4" ...attributes>
    {{yield}}
  </div>
</template>;

interface DrawerFooterSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const DrawerFooter: TOC<DrawerFooterSignature> = <template>
  <div
    data-test-drawer-footer
    class="flex-none px-6 py-4 border-t tio-border-default dark:border-gray-700"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

// ---------------------------------------------------------------------------
// Placement & size config
// ---------------------------------------------------------------------------

type Placement = 'left' | 'right' | 'top' | 'bottom';
type Size = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASSES: Record<Placement, Record<Size, string>> = {
  right: {
    sm: 'w-80',
    md: 'w-96',
    lg: 'w-[32rem]',
    xl: 'w-[42rem]',
    full: 'w-screen',
  },
  left: {
    sm: 'w-80',
    md: 'w-96',
    lg: 'w-[32rem]',
    xl: 'w-[42rem]',
    full: 'w-screen',
  },
  top: {
    sm: 'h-48',
    md: 'h-64',
    lg: 'h-96',
    xl: 'h-[32rem]',
    full: 'h-screen',
  },
  bottom: {
    sm: 'h-48',
    md: 'h-64',
    lg: 'h-96',
    xl: 'h-[32rem]',
    full: 'h-screen',
  },
};

const PLACEMENT_POSITION: Record<Placement, string> = {
  right: 'inset-y-0 right-0',
  left: 'inset-y-0 left-0',
  top: 'inset-x-0 top-0',
  bottom: 'inset-x-0 bottom-0',
};

/**
 * The closed (off-screen) translate and the axis constraint for each placement.
 */
const PLACEMENT_SLIDE_CLOSED: Record<Placement, string> = {
  right: 'translate-x-full',
  left: '-translate-x-full',
  top: '-translate-y-full',
  bottom: 'translate-y-full',
};

const PLACEMENT_SLIDE_OPEN: Record<Placement, string> = {
  right: 'translate-x-0',
  left: 'translate-x-0',
  top: 'translate-y-0',
  bottom: 'translate-y-0',
};

/** Full height/width constraint so the drawer fills its axis */
const PLACEMENT_FILL: Record<Placement, string> = {
  right: 'h-full',
  left: 'h-full',
  top: 'w-full',
  bottom: 'w-full',
};

// ---------------------------------------------------------------------------
// Main Drawer component
// ---------------------------------------------------------------------------

/**
 * Reimagined Drawer - A slide-out panel without Frontile dependency
 *
 * Renders a slide-in drawer overlay with backdrop, keyboard (Escape) support,
 * optional close-on-outside-click, and named-block Header/Body/Footer sections.
 *
 * @example Basic right drawer
 * ```gts
 * <Drawer
 *   @isOpen={{this.isDrawerOpen}}
 *   @onClose={{this.closeDrawer}}
 *   @size="lg"
 * >
 *   <:header>
 *     <h2 class="tio-text-lg-semibold">Details</h2>
 *   </:header>
 *   <:body>
 *     <p>Drawer content goes here.</p>
 *   </:body>
 *   <:footer>
 *     <button type="button" {{on "click" this.closeDrawer}}>Close</button>
 *   </:footer>
 * </Drawer>
 * ```
 *
 * @example Left placement, small
 * ```gts
 * <Drawer @isOpen={{this.open}} @onClose={{this.close}} @placement="left" @size="sm">
 *   <:body>
 *     <p>Side nav content</p>
 *   </:body>
 * </Drawer>
 * ```
 */
interface DrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer requests to close */
    onClose: () => void;
    /** Slide-in direction @default "right" */
    placement?: Placement;
    /** Width/height of the drawer @default "md" */
    size?: Size;
    /** Close when clicking the backdrop @default true */
    closeOnOutsideClick?: boolean;
    /** Close when pressing Escape @default true */
    closeOnEscapeKey?: boolean;
    /** Show the default close button in the top-right corner @default true */
    allowCloseButton?: boolean;
    /** Show the semi-transparent backdrop behind the drawer @default true */
    showBackdrop?: boolean;
    /** Manage browser history (pushState on open, back() on close) @default true
     *  Set to false when the drawer modifies query params on close to avoid
     *  history.back() racing with Ember's URL update. */
    manageHistory?: boolean;
  };
  Blocks: {
    /** Default block — free-form content (use DrawerHeader/Body/Footer directly) */
    default?: [];
    /** Named block for header content */
    header?: [];
    /** Named block for body content */
    body?: [];
    /** Named block for footer content */
    footer?: [];
  };
  Element: HTMLDivElement;
}

export default class Drawer extends Component<DrawerSignature> {
  /**
   * Internal visibility state that drives the CSS transition.
   * We keep the DOM mounted briefly after @isOpen becomes false
   * so the slide-out animation can play before we remove the element.
   */
  @tracked private _mounted = false;
  @tracked private _visible = false;

  /** Tracks whether the drawer was closed by the browser back button */
  private _closedViaPopstate = false;

  /** Unique ID for this drawer instance — used to scope querySelector */
  private drawerId = crypto.randomUUID();

  /** Portal target — renders drawer at document.body to escape stacking contexts.
   *  In test mode, portal into #ember-testing so test helpers can find
   *  portaled elements via the normal find/click/assert.dom APIs. */
  get portalDestination() {
    return document.getElementById('ember-testing') ?? document.body;
  }

  private get placement() {
    return this.args.placement ?? 'right';
  }

  private get size() {
    return this.args.size ?? 'md';
  }

  private get closeOnOutsideClick() {
    return this.args.closeOnOutsideClick ?? true;
  }

  private get closeOnEscapeKey() {
    return this.args.closeOnEscapeKey ?? true;
  }

  private get showCloseButton() {
    return this.args.allowCloseButton ?? true;
  }

  private get manageHistory() {
    return this.args.manageHistory ?? true;
  }

  private get backdropClass() {
    return this.args.showBackdrop === false ? '' : 'bg-black/40';
  }

  // Derived classes
  private get panelPositionClass() {
    return PLACEMENT_POSITION[this.placement];
  }

  private get panelSizeClass() {
    return SIZE_CLASSES[this.placement]?.[this.size] ?? SIZE_CLASSES.right.md;
  }

  private get panelFillClass() {
    return PLACEMENT_FILL[this.placement];
  }

  private get slideClass() {
    return this._visible
      ? PLACEMENT_SLIDE_OPEN[this.placement]
      : PLACEMENT_SLIDE_CLOSED[this.placement];
  }

  // --------------------------------------------------
  // Lifecycle modifier — mounts/unmounts with animation
  // --------------------------------------------------

  /**
   * Manages mount/unmount lifecycle to allow CSS transitions.
   * When `isOpen` becomes true, we mount immediately then
   * defer the visible flag to the next frame so the browser
   * can paint the initial (off-screen) state first.
   *
   * When `isOpen` becomes false, we set visible=false to trigger
   * the slide-out, then unmount after the transition duration.
   */
  manageFocus = modifier((_element: HTMLElement, [isOpen]: [boolean]) => {
    let rafId: number | undefined;
    // Capture scroll position so the teardown can restore it if the modifier
    // is torn down before the close path runs (e.g. route transition).
    let savedScrollY = 0;

    // Browser back-button integration — hoisted so teardown can access them.
    let historyDrawerId: string | undefined;
    let didPushHistory = false;
    const onPopstate = (event: PopStateEvent) => {
      // If we're navigating TO our own pushed entry (another drawer above us in
      // history called back() and landed on our state), our drawer is still open.
      // Don't close — only close when navigating AWAY from our entry.
      if (event.state?.drawerId === historyDrawerId) return;
      this._closedViaPopstate = true;
      window.removeEventListener('popstate', onPopstate);
      this.args.onClose();
    };

    if (isOpen) {
      this._mounted = true;

      // Lock body scroll — preserve scroll position using the fixed-body pattern
      // so the page doesn't jump when overflow is hidden.
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      // Trigger the slide-in on the next frame
      rafId = requestAnimationFrame(() => {
        rafId = undefined;
        this._visible = true;

        // Focus the dialog container for keyboard nav.
        // Scope to the modifier element to avoid selecting a different drawer.
        // preventScroll avoids the browser scrolling to the focused element.
        const dialog = document.querySelector<HTMLElement>(`[data-drawer-id="${this.drawerId}"]`);
        if (dialog) {
          dialog.focus({ preventScroll: true });
        }
      });

      // Push a history entry so the back button closes the drawer instead
      // of navigating away. Capture whether we installed history integration
      // so teardown always cleans up what was set up, even if @manageHistory
      // changes while the drawer is open.
      didPushHistory = false;
      if (this.manageHistory) {
        didPushHistory = true;
        historyDrawerId = this.drawerId;
        this._closedViaPopstate = false;
        history.pushState({ drawerId: historyDrawerId }, '');
        window.addEventListener('popstate', onPopstate);
      }
    } else if (this._mounted) {
      this._visible = false;

      // Wait for the CSS transition to finish before un-mounting
      const cleanup = setTimeout(() => {
        this._mounted = false;
      }, 300); // matches transition duration

      // Scroll restore happens in the teardown below so it only runs once,
      // regardless of whether the close path or a route-transition triggers it.
      return () => clearTimeout(cleanup);
    }

    return () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }

      // Clean up browser history integration
      if (didPushHistory) {
        window.removeEventListener('popstate', onPopstate);
        if (
          historyDrawerId &&
          !this._closedViaPopstate &&
          !this.args.isOpen &&
          history.state?.drawerId === historyDrawerId
        ) {
          // Drawer closed programmatically (X, Escape, backdrop) — remove
          // the history entry we pushed so the user doesn't have a phantom
          // back-button press.
          history.back();
        }
        this._closedViaPopstate = false;
      }

      // Restore body scroll — single restore point for both normal close
      // and unexpected teardown (route transition, component destroy).
      if (document.body.style.position === 'fixed') {
        const scrollY = savedScrollY || Math.abs(parseInt(document.body.style.top || '0', 10));
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      }
    };
  });

  // --------------------------------------------------
  // Event handlers
  // --------------------------------------------------

  handleBackdropClick = () => {
    if (this.closeOnOutsideClick) {
      this.args.onClose();
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (this.closeOnEscapeKey && event.key === 'Escape') {
      event.stopPropagation();
      this.args.onClose();
    }
  };

  handleCloseButton = () => {
    this.args.onClose();
  };

  <template>
    {{! Anchor element for the lifecycle modifier — always in DOM }}
    <div
      class="reimagined-drawer-anchor sr-only"
      {{this.manageFocus @isOpen}}
      aria-hidden="true"
    ></div>

    {{#if this._mounted}}
      {{#in-element this.portalDestination insertBefore=null}}
        {{! Fullscreen overlay wrapper }}
        <div
          data-test-drawer
          data-drawer-id={{this.drawerId}}
          class="fixed inset-0 z-40 flex"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          {{on "keydown" this.handleKeyDown}}
          ...attributes
        >
          {{! Backdrop }}
          {{#if this.closeOnOutsideClick}}
            {{! template-lint-disable no-nested-interactive }}
            {{! template-lint-disable no-bare-strings }}
            <button
              data-test-drawer-backdrop
              type="button"
              class="absolute inset-0
                {{this.backdropClass}}
                transition-opacity duration-300 cursor-default
                {{if this._visible 'opacity-100' 'opacity-0'}}"
              aria-label="Close"
              tabindex="-1"
              {{on "click" this.handleBackdropClick}}
            ></button>
          {{else}}
            <div
              data-test-drawer-backdrop
              class="absolute inset-0
                {{this.backdropClass}}
                transition-opacity duration-300 cursor-default
                {{if this._visible 'opacity-100' 'opacity-0'}}"
              aria-hidden="true"
            ></div>
          {{/if}}

          {{! Drawer panel }}
          <div
            data-test-drawer-panel
            class="absolute
              {{this.panelPositionClass}}
              {{this.panelSizeClass}}
              {{this.panelFillClass}}
              flex flex-col bg-white dark:bg-gray-900 shadow-2xl transform transition-transform duration-300 ease-in-out
              {{this.slideClass}}"
          >
            {{! Close button }}
            {{#if this.showCloseButton}}
              <button
                data-test-drawer-close
                type="button"
                class="absolute top-3 right-3 z-10 p-1.5 rounded-lg tio-fg-disabled hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                {{! template-lint-disable no-bare-strings }}
                aria-label="Close drawer"
                {{on "click" this.handleCloseButton}}
              >
                <PhX @size={{20}} />
              </button>
            {{/if}}

            {{! Named block API — header / body / footer }}
            {{#if (has-block "header")}}
              <DrawerHeader>{{yield to="header"}}</DrawerHeader>
            {{/if}}

            {{#if (has-block "body")}}
              <DrawerBody>{{yield to="body"}}</DrawerBody>
            {{/if}}

            {{#if (has-block "footer")}}
              <DrawerFooter>{{yield to="footer"}}</DrawerFooter>
            {{/if}}

            {{! Default block — child manages own layout with DrawerHeader/Body/Footer }}
            {{#if (has-block)}}
              {{yield}}
            {{/if}}
          </div>
        </div>
      {{/in-element}}
    {{/if}}
  </template>
}

export { DrawerHeader, DrawerBody, DrawerFooter };
export type { DrawerSignature, Placement, Size };
